/**
When on PDP (caveat: no quantity selector on the PDP)
1. A line item value is generated when the product form component is built
2. When user clicks gift wrap plus button, an `input type=hidden` is created, with name = 'properties[Gift wrapping]' and `value=value from step 1`
2b. If the user clicks the `remove gift wrap` button, the hidden input is deleted
3. When the user clicks add to cart, if the product form has the correct property (ie, the gift wrapping property), the gift wrap product is manually added to cart, with a line item property that matches the original product

When on a product card: no option to add gift wrap

From cart/mini cart line item
1. handle checkbox
a) should add/remove the gift wrap product
b) should add/remove gift wrap line item
2. handle quantity selector 
a) should add/remove the gift wrap product
b) should add/remove gift wrap line item
3. handle remove button 
a) should remove corresponding gift wrap product as well

 */

import { utils, LitElement } from '@hh/global.js'
const {
  changeLineItem,
  addToCart,
  updateCart,
  reRenderShopifySections,
  getInvalidGiftWrapCartLines,
} = utils

class HHGiftWrap extends LitElement {
  static properties = {
    // id of main product
    productId: {
      type: Number,
    },
    // index of line item
    lineID: {
      type: String,
    },
    // gift wrap product id
    giftWrapProductId: {
      type: String,
    },
    // whether the current product *is* gift wrap
    isGiftWrap: {
      type: Boolean,
      attribute: 'is-gift-wrap',
    },
    // whether the current product *already* has gift wrap connected
    hasGiftWrap: {
      type: Boolean,
      reflect: true,
      attribute: 'has-gift-wrap',
    },
    // line item gift wrap property value (timestamp)
    lineItemValue: {
      type: Number,
    },
    // line item quantity
    quantity: {
      type: Number,
    },
    lineItemPropertiesEncoded: {
      type: String,
    },
    lineItemEntityDescriptionKey: {
      type: String,
    },
    lineItemEntityDescriptionValue: {
      type: String,
    },
  }

  constructor() {
    super()

    this.dateStamp = Date.now()
    this.inlineCartSectionId =
      document.getElementById('inline-cart')?.dataset.id || 'inline-cart'
  }

  connectedCallback() {
    this.addEventListener(
      'handleGiftWrapFromCart',
      this.handleGiftWrapFromCart.bind(this)
    )

    const LIPData = JSON.parse(this.querySelector('[data-line-item-properties]').textContent) || []
    this.lineItemProperties = Object.fromEntries(LIPData) ?? {}
  }

  disconnectedCallback() {
    this.removeEventListener(
      'handleGiftWrapFromCart',
      this.handleGiftWrapFromCart
    )
  }

  // when the checkbox in the cart line item is toggled
  async handleGiftWrapFromCart(e) {
    let updateCartResponse

    if (e.detail.value) {
      this.closest('hh-mini-cart')?.setAttribute('loading', true)
      this.closest('hh-inline-cart')?.setAttribute('loading', true)

      updateCartResponse = await this.addLIP(e.detail.lineID)
      updateCartResponse = await this.addGiftWrap(this.giftWrapProductId)

      document.querySelector('hh-mini-cart')?.removeAttribute('loading')
      document.querySelector('hh-inline-cart')?.removeAttribute('loading')
    } else {
      this.closest('hh-mini-cart')?.setAttribute('loading', true)
      this.closest('hh-inline-cart')?.setAttribute('loading', true)
      const target_line_item_value = e.detail.lineItemValue

      updateCartResponse = await this.removeGiftWrapProduct(target_line_item_value)
      updateCartResponse = await this.removeLIP(target_line_item_value)

      document.querySelector('hh-mini-cart')?.removeAttribute('loading')
      document.querySelector('hh-inline-cart')?.removeAttribute('loading')
    }

    if (updateCartResponse) {
      reRenderShopifySections(updateCartResponse)
      window.Nosto?.reloadCart()
    }
  }

  // add line item property to the product (ie, the one that's getting gift wrapped)
  async addLIP(line_id) {
    const properties = {
      ...this.lineItemProperties,
      'Gift wrapping': `${this.dateStamp}`,
      [this.lineItemEntityDescriptionKey]:
        this.lineItemEntityDescriptionValue,
      '_gift-wrap-key': this.lineItemEntityDescriptionKey,
      '_gift-wrap-value': this.lineItemEntityDescriptionValue,
    }

    delete properties['_gift_wrap_removed']

    const body = {
      id: line_id,
      quantity: this.quantity,
      properties
    }

    return await changeLineItem(body, `${this.inlineCartSectionId},header,mini-cart`)
  }

  // add gift wrap products, with corresponding line item property
  async addGiftWrap(gift_wrap_products_id) {
    const gift_wrap_products = gift_wrap_products_id.split(',')

    const body = {
      items: [],
    }

    gift_wrap_products.forEach((el) => {
      body.items.push({
        id: el,
        quantity: this.quantity,
        properties: {
          'Gift wrapping': `${this.dateStamp}`,
          [this.lineItemEntityDescriptionKey]:
            this.lineItemEntityDescriptionValue,
          _is_gift_wrap: 'true',
          '_gift-wrap-key': this.lineItemEntityDescriptionKey,
          '_gift-wrap-value': this.lineItemEntityDescriptionValue,
        },
      })
    })

    return await addToCart(body, `${this.inlineCartSectionId},header,mini-cart`)
  }

  // remove gift wrap product that has current product's line item value
  async removeGiftWrapProduct(target_line_item_value) {
    const target_products = [
      ...document.querySelectorAll(
        `hh-gift-wrap[is-gift-wrap][lineItemValue='${target_line_item_value}']`
      ),
    ]?.map((item) => item.getAttribute('lineid'))

    const body = {
      updates: {},
    }

    target_products.forEach((el) => {
      body.updates[el] = 0
    })

    let updateCartResponse = await updateCart(
      body,
      `${this.inlineCartSectionId},header,mini-cart`
    )

    const invalidGiftWrapCartLines =
      getInvalidGiftWrapCartLines(updateCartResponse)
    if (Object.keys(invalidGiftWrapCartLines).length) {
      updateCartResponse = await updateCart(
        { updates: invalidGiftWrapCartLines },
        `${this.inlineCartSectionId},header,mini-cart`
      )
    }

    return updateCartResponse
  }

  async removeLIP(target_line_item_value) {
    const target_product = document.querySelector(
      `hh-gift-wrap[has-gift-wrap][lineItemValue='${target_line_item_value}']`
    ).lineID
    const body = {
      id: target_product,
      quantity: this.quantity,
      properties: {
        ...this.lineItemProperties,
        '_gift_wrap_removed': 'true',
      },
    }

    delete body.properties['_gift-wrap-key']
    delete body.properties['_gift-wrap-value']
    delete body.properties['Gift wrapping']
    delete body.properties[this.lineItemEntityDescriptionKey]

    return await changeLineItem(body, `${this.inlineCartSectionId},header,mini-cart`)
  }
}

customElements.define('hh-gift-wrap', HHGiftWrap)
